import React, { useEffect, useState } from 'react';
import { AlertCircle, CreditCard, Loader2, X } from 'lucide-react';
import {
  installmentScheduleTotal,
  normalizeInstallmentShares,
  validateSupplierPaymentTerms,
  type SupplierBillCategory,
  type SupplierPaymentTermsSpec,
} from '../../utils/supplierBills';
import SupplierPaymentTermsEditor, { defaultSupplierPaymentTerms } from './SupplierPaymentTermsEditor';
import './SupplierPaymentTermsModal.css';

export interface SupplierPaymentTermsModalProps {
  open: boolean;
  vendorName: string;
  /** e.g. "Timber quote — White Oak — $48,200". */
  quoteLabel: string;
  billAmount: number;
  /** Timber project id — scopes the task picker for task-anchored payments. */
  projectId?: string;
  initialTerms?: SupplierPaymentTermsSpec | null;
  /** Timber/CNC bills show a freight-included toggle. */
  billCategory?: SupplierBillCategory;
  /** Installment ids already approved to pay or marked paid. */
  lockedInstallmentIds?: string[];
  /** Installment ids already marked paid. */
  paidInstallmentIds?: string[];
  onSave: (terms: SupplierPaymentTermsSpec) => Promise<void>;
  /** Skip — keep the bill as a single payment with no breakdown. */
  onClose: () => void;
}

function formatMoney(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

/**
 * Payment breakdown entered by the approver when a supplier quote is awarded.
 * Each supplier is different — 50% down / 50% at completion, full payment
 * net 30, early-pay discounts, etc. The breakdown drives AP timing in the
 * cash flow projection.
 */
const SupplierPaymentTermsModal: React.FC<SupplierPaymentTermsModalProps> = ({
  open,
  vendorName,
  quoteLabel,
  billAmount,
  projectId,
  initialTerms,
  billCategory,
  lockedInstallmentIds = [],
  paidInstallmentIds = [],
  onSave,
  onClose,
}) => {
  const [terms, setTerms] = useState<SupplierPaymentTermsSpec | null>(null);
  const [scheduleAmount, setScheduleAmount] = useState(billAmount);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setScheduleAmount(billAmount);
    setTerms(
      initialTerms && initialTerms.installments.length > 0
        ? initialTerms
        : defaultSupplierPaymentTerms(billAmount),
    );
  }, [open, initialTerms, billAmount]);

  const handleSave = async () => {
    if (!terms) return;
    const amount = installmentScheduleTotal(terms.installments, scheduleAmount || billAmount);
    const ready = amount > 0
      ? {
        ...terms,
        installments: normalizeInstallmentShares(
          terms.installments,
          amount,
          new Set(lockedInstallmentIds),
        ),
      }
      : terms;
    const check = validateSupplierPaymentTerms(ready, amount > 0 ? amount : undefined);
    if (!check.ok) {
      setError(check.message);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(ready);
      onClose();
    } catch (err) {
      console.error('[SupplierPaymentTermsModal] save failed', err);
      setError(err instanceof Error ? err.message : 'Could not save payment terms.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="ops-modal-overlay sptm-overlay" role="presentation" onClick={saving ? undefined : onClose}>
      <div
        className="ops-modal sptm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sptm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ops-modal-header sptm-header">
          <div className="sptm-title-block">
            <CreditCard size={18} aria-hidden className="sptm-title-icon" />
            <h3 id="sptm-title">Payment terms — {vendorName}</h3>
          </div>
          <button type="button" className="ops-modal-close" aria-label="Close" onClick={onClose} disabled={saving}>
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="ops-modal-body sptm-body">
          <p className="sptm-lead">
            How does <strong>{vendorName}</strong> expect to be paid for <strong>{quoteLabel}</strong>
            {(scheduleAmount || billAmount) > 0 ? <> ({formatMoney(scheduleAmount || billAmount)})</> : null}? These installments feed the
            cash flow projection.
          </p>

          <SupplierPaymentTermsEditor
            billAmount={billAmount}
            projectId={projectId}
            value={terms}
            onChange={setTerms}
            disabled={saving}
            compact
            billCategory={billCategory}
            lockedInstallmentIds={lockedInstallmentIds}
            paidInstallmentIds={paidInstallmentIds}
            onScheduleAmountChange={setScheduleAmount}
          />

          {error && (
            <p className="sptm-error" role="alert">
              <AlertCircle size={14} aria-hidden /> {error}
            </p>
          )}
        </div>

        <footer className="ops-modal-footer sptm-footer">
          <button type="button" className="ops-hide-completed-btn" onClick={onClose} disabled={saving}>
            Skip — single due date
          </button>
          <button type="button" className="ops-new-btn" disabled={saving} onClick={() => void handleSave()}>
            {saving ? (
              <>
                <Loader2 size={14} className="sptm-spin" aria-hidden /> Saving…
              </>
            ) : (
              'Save payment terms'
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default SupplierPaymentTermsModal;
